import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  Col,
  ConfirmPopoverButton,
  DatePicker,
  DateValue,
  Kbd,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  TouchButton,
} from '@rematter/ui-web';
import {
  LucideCheckCircle,
  LucidePackageCheck,
  LucideShip,
  LucideUndo2,
} from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';
import { LOAD_STATUS_ORDER } from '../../../utils/status';
import { VoidTicketButton } from '../shared/VoidTicketButton';

type OutboundHeaderActionsProps = {
  status: LoadStatus;
  isProcessing: boolean;
  reviewed: boolean;
  onShip: () => void;
  onVoid: (voidReason?: string) => void;
  onRevert: (targetStatus: LoadStatus) => void;
  onReceive: (receivedDate: CalendarDate) => void;
  onReconcile: () => void;
};

type ShipButtonProps = {
  status: LoadStatus;
  isProcessing: boolean;
  onShip: () => void;
};

type RevertButtonProps = {
  status: LoadStatus;
  isProcessing: boolean;
  onRevert: (targetStatus: LoadStatus) => void;
};

type ReceiveButtonProps = {
  status: LoadStatus;
  isProcessing: boolean;
  onReceive: (receivedDate: CalendarDate) => void;
};

type ReconcileButtonProps = {
  status: LoadStatus;
  isProcessing: boolean;
  reviewed: boolean;
  onReconcile: () => void;
};

//
// ⚛️ ⚛️ ⚛️ SHIP BUTTON ⚛️ ⚛️ ⚛️
//
const ShipButton = ({ status, isProcessing, onShip }: ShipButtonProps) => {
  const t = useTranslations();

  if (
    status !== enumLoadStatus.Unassigned &&
    status !== enumLoadStatus.Pending &&
    status !== enumLoadStatus.ContainerReadyToShip
  )
    return null;

  return (
    <TouchButton
      variant="solid"
      color="primary"
      size="xl"
      className="@ShipButton h-full"
      onPress={onShip}
      isDisabled={isProcessing}
    >
      <Row gap1>
        <Kbd shortcut="Alt+F" />
        <LucideShip size={16} />
        {t.scaleTicketPro.outboundDetail.ship}
      </Row>
    </TouchButton>
  );
};

//
// ⚛️ ⚛️ ⚛️ REVERT BUTTON ⚛️ ⚛️ ⚛️
//
const RevertButton = ({
  status,
  isProcessing,
  onRevert,
}: RevertButtonProps) => {
  const t = useTranslations();

  // States
  const [isOpen, setIsOpen] = React.useState(false);

  const revertLabel: Record<string, string> = {
    [enumLoadStatus.Unassigned]: t.scaleTicketPro.statusUnassigned,
    [enumLoadStatus.Pending]: t.scaleTicketPro.statusPending,
    [enumLoadStatus.ContainerReadyToShip]:
      t.scaleTicketPro.statusContainerReadyToShip,
    [enumLoadStatus.Shipped]: t.scaleTicketPro.statusShipped,
    [enumLoadStatus.Received]: t.scaleTicketPro.statusReceived,
  };

  const currentOrder = LOAD_STATUS_ORDER[status] ?? -1;

  if (currentOrder <= 0 || status === enumLoadStatus.Voided) return null;

  const revertTargets = Object.entries(LOAD_STATUS_ORDER)
    .filter(([, order]) => order < currentOrder)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="@RevertButton h-full">
      <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <TouchButton
          variant="outline"
          size="xl"
          className="h-full"
          isDisabled={isProcessing}
          onPress={() => null}
        >
          <Row gap1>
            <LucideUndo2 size={16} /> {t.scaleTicketPro.outboundDetail.revert}
          </Row>
        </TouchButton>
        <Popover>
          <PopoverDialog>
            <Col gap2 className="min-w-[200px] p-3">
              <div className="text-sm font-semibold text-neutral">
                {t.scaleTicketPro.outboundDetail.revertToStatus}
              </div>
              {revertTargets.map(([targetStatus]) => (
                <TouchButton
                  key={targetStatus}
                  variant="outline"
                  size="md"
                  className="w-full justify-start"
                  isDisabled={isProcessing}
                  onPress={() => {
                    onRevert(targetStatus as LoadStatus);
                    setIsOpen(false);
                  }}
                >
                  {revertLabel[targetStatus] ?? targetStatus}
                </TouchButton>
              ))}
            </Col>
          </PopoverDialog>
        </Popover>
      </PopoverTrigger>
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ RECEIVE BUTTON ⚛️ ⚛️ ⚛️
//
const ReceiveButton = ({
  status,
  isProcessing,
  onReceive,
}: ReceiveButtonProps) => {
  const t = useTranslations();

  // States
  const [isOpen, setIsOpen] = React.useState(false);
  const [receivedDate, setReceivedDate] = React.useState<DateValue | null>(
    null,
  );

  if (status !== enumLoadStatus.Shipped) return null;

  return (
    <div className="@ReceiveButton h-full">
      <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <TouchButton
          variant="solid"
          color="primary"
          size="xl"
          className="h-full"
          isDisabled={isProcessing}
          onPress={() => null}
        >
          <Row gap1>
            <LucidePackageCheck size={16} />
            {t.loadStatusTransition.markAsReceivedPopover.markAsReceived}
          </Row>
        </TouchButton>
        <Popover>
          <PopoverDialog>
            <Col gap2 className="w-[320px] p-4">
              <DatePicker
                value={receivedDate}
                onChange={setReceivedDate}
                className="w-full"
                label={
                  t.loadStatusTransition.markAsReceivedPopover.receivedDate
                }
                isRequired
              />
              <Row gap2 className="justify-end">
                <TouchButton
                  variant="outline"
                  size="md"
                  onPress={() => {
                    setReceivedDate(null);
                    setIsOpen(false);
                  }}
                >
                  {t.common.cancel}
                </TouchButton>
                <TouchButton
                  variant="solid"
                  color="primary"
                  size="md"
                  isDisabled={isProcessing || !receivedDate}
                  onPress={() => {
                    if (receivedDate) {
                      setIsOpen(false);
                      onReceive(
                        new CalendarDate(
                          receivedDate.year,
                          receivedDate.month,
                          receivedDate.day,
                        ),
                      );
                    }
                  }}
                >
                  {t.loadStatusTransition.markAsReceivedPopover.markAsReceived}
                </TouchButton>
              </Row>
            </Col>
          </PopoverDialog>
        </Popover>
      </PopoverTrigger>
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ RECONCILE BUTTON ⚛️ ⚛️ ⚛️
//
const ReconcileButton = ({
  status,
  isProcessing,
  reviewed,
  onReconcile,
}: ReconcileButtonProps) => {
  const t = useTranslations();

  if (status !== enumLoadStatus.Received || !reviewed) return null;

  return (
    <ConfirmPopoverButton
      onConfirm={onReconcile}
      message={t.loadStatusTransition.reconciliation.description}
      confirmLabel={t.loadStatusTransition.reconciliation.reconcile}
      cancelLabel={t.common.cancel}
      color="success"
      className="@ReconcileButton h-full"
      contentClassName="max-w-80"
      renderTrigger={() => (
        <TouchButton
          variant="solid"
          color="success"
          isDisabled={isProcessing}
          size="xl"
          className="h-full"
          onPress={() => null}
        >
          <Row gap1>
            <LucideCheckCircle size={16} />
            {t.loadStatusTransition.reconciliation.reconcile}
          </Row>
        </TouchButton>
      )}
      isLoading={isProcessing}
    />
  );
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET HEADER ACTIONS ⚛️ ⚛️ ⚛️
//
export const OutboundTicketHeaderActions = ({
  status,
  isProcessing,
  reviewed,
  onShip,
  onVoid,
  onRevert,
  onReceive,
  onReconcile,
}: OutboundHeaderActionsProps) => (
  <Row gap2 className="@OutboundTicketHeaderActions h-full">
    {status !== enumLoadStatus.Voided &&
      status !== enumLoadStatus.Reconciled && (
        <VoidTicketButton
          isDisabled={isProcessing || status === enumLoadStatus.Shipped}
          isProcessing={isProcessing}
          onVoid={onVoid}
          className="@VoidButton h-full"
        />
      )}
    <RevertButton
      status={status}
      onRevert={onRevert}
      isProcessing={isProcessing}
    />
    <ShipButton status={status} onShip={onShip} isProcessing={isProcessing} />
    <ReceiveButton
      status={status}
      onReceive={onReceive}
      isProcessing={isProcessing}
    />
    <ReconcileButton
      status={status}
      onReconcile={onReconcile}
      isProcessing={isProcessing}
      reviewed={reviewed}
    />
  </Row>
);
